import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Send, Loader2 } from "lucide-react";
import FileAttachmentPreview, {
  FileAttachment,
} from "@/polymet/components/file-attachment-preview";

interface MessageComposerProps {
  onSendMessage: (
    message: string,
    attachments: FileAttachment[]
  ) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  maxAttachmentSize?: number; // in bytes
  maxAttachments?: number;
  allowedFileTypes?: string[];
}

export default function MessageComposer({
  onSendMessage,
  placeholder = "Type your message...",
  disabled = false,
  maxAttachmentSize = 10 * 1024 * 1024, // 10MB default
  maxAttachments = 5,
  allowedFileTypes = [
    "image/*",
    "application/pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ppt",
    ".pptx",
    ".zip",
    ".rar",
  ],
}: MessageComposerProps) {
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAttachFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Check if adding these files would exceed the max attachments limit
    if (attachments.length + files.length > maxAttachments) {
      setError(`You can only attach up to ${maxAttachments} files`);
      return;
    }

    setError(null);
    const newAttachments: FileAttachment[] = [];

    Array.from(files).forEach((file) => {
      // Check file size
      if (file.size > maxAttachmentSize) {
        setError(
          `File "${file.name}" exceeds the maximum size of ${maxAttachmentSize / (1024 * 1024)}MB`
        );
        return;
      }

      // Create a URL for image preview
      let fileUrl;
      if (file.type.startsWith("image/")) {
        fileUrl = URL.createObjectURL(file);
      }

      newAttachments.push({
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        url: fileUrl,
      });
    });

    setAttachments((prev) => [...prev, ...newAttachments]);

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => {
      const updatedAttachments = prev.filter(
        (attachment) => attachment.id !== id
      );
      if (updatedAttachments.length < maxAttachments) {
        setError(null);
      }
      return updatedAttachments;
    });
  };

  const handleSubmit = async () => {
    if (!message.trim() && attachments.length === 0) return;

    try {
      setIsSubmitting(true);
      await onSendMessage(message, attachments);
      setMessage("");
      setAttachments([]);
      setError(null);
    } catch (err) {
      setError("Failed to send message. Please try again.");
      console.error("Error sending message:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border rounded-md p-4 bg-card">
      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="mb-3 space-y-2">
          {attachments.map((file) => (
            <FileAttachmentPreview
              key={file.id}
              file={file}
              onRemove={handleRemoveAttachment}
            />
          ))}
        </div>
      )}

      {/* Error message */}
      {error && <div className="mb-3 text-sm text-destructive">{error}</div>}

      <div className="flex gap-2">
        <div className="flex-1">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSubmitting}
            className="min-h-[80px] resize-none"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleAttachFile}
            disabled={
              disabled || isSubmitting || attachments.length >= maxAttachments
            }
            title="Attach file"
          >
            <Paperclip className="h-4 w-4" />
            <span className="sr-only">Attach file</span>
          </Button>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              disabled ||
              isSubmitting ||
              (!message.trim() && attachments.length === 0)
            }
            size="icon"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span className="sr-only">Send message</span>
          </Button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        accept={allowedFileTypes.join(",")}
        className="hidden"
      />

      {/* Helper text */}
      <div className="mt-2 text-xs text-muted-foreground">
        {`Max ${maxAttachments} files (${maxAttachmentSize / (1024 * 1024)}MB each)`}
      </div>
    </div>
  );
}
